import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import {
  IResponseData,
  IMetaData,
  IPourEvent,
  IMixDesign,
  IRenamePourEvent,
  IPourEventHistory,
  IError,
  IApiParams,
  IUpcomingPourEventResponse,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const pourEventsQueryKey = 'getPourEvents';
const pourEventHistoryKey = 'getPourEventHistory';
const pourEventMixDesignQueryKey = 'getPourEventMixDesign';
const pourEventQueryKey = 'renamePourEvent';
const upcomingPourEventKey = 'getUpcomingPourEvent';

export function usePourEventsApi({
  queryParameters,
  organizationId,
}: IApiParams) {
  const authClient = useAuthClient();
  const queryClient = useQueryClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getPourEventsUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/PourEvents/get-project-pour-events-paged`;
  }, [currentSupplierId]);

  const getPourEventHistoryUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/PourEvents/get-pour-event-history`;
  }, [currentSupplierId]);

  const getUpcomingPourEventUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/PourEvents/get-upcoming-pour-event`;
  }, [currentSupplierId]);

  const getPourEventMixDesigns = useMemo(() => {
    return `/api/v1/${currentSupplierId}/PourEvents/get-mix-design-details`;
  }, [currentSupplierId]);

  const renamePourEventUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/PourEvents/rename-pour-event`;
  }, [currentSupplierId]);

  const {
    mutateAsync: mutatePourEvent,
    isPending: isMutatingPourEvent,
    error: mutatePourEventError,
  } = useMutation({
    mutationFn: (payload: IRenamePourEvent) =>
      authClient(renamePourEventUrl, { method: 'PATCH', data: payload }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [pourEventQueryKey],
      });
      return queryClient.getQueryData<IResponseData<boolean>>([
        pourEventQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<boolean>>(
          [pourEventQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(errors.renamePourEvent, error, {
        renamePourEventUrl,
        ...variables,
      });
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [pourEventQueryKey],
      });
    },
  });

  const {
    data: pourEventMixDesignResponse,
    refetch: fetchPourEventMixDesign,
    isLoading: isFetchingPourEventMixDesign,
    error: fetchPourEventMixDesignError,
  } = useQueryWithErrorHandler<IResponseData<IMixDesign[]>, IError>({
    queryKey: [pourEventMixDesignQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getPourEventMixDesigns}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: (error: IError) => {
      firebaseAnalytics.logErrorEvent(errors.getPourEventMixDesigns, error, {
        getPourEventMixDesigns,
        ...queryParameters,
      });
      openModal(error);
    },
  });

  const {
    data: pourEventsResponse,
    refetch: fetchPourEvents,
    isLoading: isFetchingPourEvents,
    error: fetchingPourEventError,
  } = useQueryWithErrorHandler<IResponseData<IPourEvent[]>, IError>({
    queryKey: [pourEventsQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getPourEventsUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getPourEventsList, error, {
        getPourEventsUrl,
        ...queryParameters,
      });
      openModal(error);
    },
  });

  const {
    data: pourEventHistoryResponse,
    refetch: fetchPourHistory,
    isFetching: isFetchingPourEventHistory,
    error: fetchPourEventHistoryError,
  } = useQueryWithErrorHandler<IResponseData<IPourEventHistory>, IError>({
    queryKey: [pourEventHistoryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getPourEventHistoryUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getPourEventHistory, error, {
        getPourEventHistoryUrl,
        ...queryParameters,
      });
      openModal(error);
    },
  });

  const {
    data: upcomingPourEventResponse,
    refetch: fetchUpcomingPourEvent,
    isFetching: isFetchingUpcomingPourEvent,
    error: fetchUpcomingPourEventError,
  } = useQueryWithErrorHandler<
    IResponseData<IUpcomingPourEventResponse>,
    IError
  >({
    queryKey: [upcomingPourEventKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${getUpcomingPourEventUrl}`, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getPourEventHistory, error, {
        getUpcomingPourEventUrl,
        ...queryParameters,
      });
      openModal(error);
    },
  });

  const pourEvents = useMemo<IPourEvent[] | undefined>(
    () => pourEventsResponse?.data,
    [pourEventsResponse],
  );

  const pourEventHistory = useMemo<IPourEventHistory | undefined>(
    () => pourEventHistoryResponse?.data,
    [pourEventHistoryResponse],
  );

  const pourEventMixDesign = useMemo<IMixDesign[] | undefined>(
    () => pourEventMixDesignResponse?.data,
    [pourEventMixDesignResponse],
  );

  const pourEventsMetadata = useMemo<IMetaData | undefined>(
    () => pourEventsResponse?.metadata,
    [pourEventsResponse],
  );

  return {
    pourEventsMetadata,
    pourEvents,
    fetchPourEvents,
    isFetchingPourEvents,
    pourEventHistoryResponse,
    fetchPourHistory,
    pourEventHistory,
    isFetchingPourEventHistory,
    fetchPourEventMixDesign,
    isFetchingPourEventMixDesign,
    pourEventMixDesignResponse,
    pourEventMixDesign,
    mutatePourEvent,
    isMutatingPourEvent,
    mutatePourEventError,
    fetchingPourEventError,
    fetchPourEventMixDesignError,
    fetchPourEventHistoryError,
    fetchUpcomingPourEvent,
    isFetchingUpcomingPourEvent,
    fetchUpcomingPourEventError,
    upcomingPourEventResponse,
  };
}
